import { useEffect, useState, useRef } from "react";
import apiService from "../api/apiservice";
import CategoryPage from "./CategoryPage";
import Background from "../Components/Background";
import { motion } from "framer-motion";
import Loader from "../Components/Loader";
import { getTopCommentsThisWeek } from "../Components/TopComments";
import List from "./List";
import MovieCard from "../Components/MovieCard";
import { collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../config/firebase";
const FADE_DURATION = 900;

const NewHome = () => {
  const [popularMovies, setPopularMovies] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(null);
  const [fade, setFade] = useState(true);
  const [loading, setLoading] = useState(true); // <-- Add loading state
  const timeoutRef = useRef();
  const [topComments, setTopComments] = useState([]);
  const [allLists, setAllLists] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchAllLists = async () => {
      try {
        const usersCollection = collection(db, "users");
        const querySnapshot = await getDocs(usersCollection);
        const allUserLists = [];

        querySnapshot.forEach((userDoc) => {
          const userData = userDoc.data();
          const userLists = userData.profile?.lists || [];

          // Add each list to the allUserLists array
          userLists.forEach((list) => {
            allUserLists.push({
              ...list,
              name: userData.profile?.name, // Track which user owns the list
            });
          });
        });

        setAllLists(allUserLists);
      } catch (error) {
        console.error("Error fetching all lists:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllLists();
  }, []);

  // Fetch movie data
  const fetchData = async () => {
    try {
      const [movies] = await Promise.all([apiService.getPopularMovies()]);
      setPopularMovies(movies);
      setLoading(false); // <-- Set loading to false after data is fetched
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false); // <-- Hide loader even if error
    }
  };

  useEffect(() => {
    const fetchTopComments = async () => {
      const comments = await getTopCommentsThisWeek();
      setTopComments(comments);
    };
    fetchTopComments();
  }, []);
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (popularMovies.length === 0) return;

    const interval = setInterval(() => {
      // Start transition: set prevIndex and next index
      setPrevIndex(currentIndex);
      setCurrentIndex((prev) => (prev + 1) % popularMovies.length);
      setFade(false); // start fade-out

      // After fade duration, finish transition
      timeoutRef.current = setTimeout(() => {
        setFade(true); // start fade-in
        setPrevIndex(null); // clear prevIndex after fade-in
      }, FADE_DURATION);
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line
  }, [popularMovies, currentIndex]);

  const movie = popularMovies[currentIndex];
  const prevMovie = prevIndex !== null ? popularMovies[prevIndex] : null;

  // Loader: show until we have a movie with a backdrop
  if (loading || !movie || !movie.backdrop_path) {
    return <Loader />;
  }

  return (
    <div className="">
      <Background />
      <div className="flex flex-col items-center justify-center relative z-20">
        <div className="relative w-screen h-screen overflow-hidden text-white">
          {/* Previous movie fades out */}
          {prevMovie && (
            <div
              className={`absolute inset-0 movie-main-backdrop transition-opacity duration-700 ease-in-out ${
                fade ? "opacity-0 pointer-events-none" : "opacity-100"
              }`}
              key={prevMovie.id}
            >
              <img
                className="w-full h-full object-cover"
                src={`https://image.tmdb.org/t/p/original${prevMovie.backdrop_path}`}
                alt="Backdrop Blurred"
              />

              <div className="absolute text-lg start-[85%] top-[50%] -translate-y-1/2 rotate-[270deg] w-max text-white opacity-70 tracking-wider">
                {prevMovie.title} ({prevMovie.release_date?.slice(0, 4)})
              </div>
            </div>
          )}

          {/* Current movie fades in */}
          {movie && (
            <div
              className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                fade ? "opacity-100" : "opacity-0"
              }`}
              key={movie.id}
            >
              <img
                className="w-full h-full object-cover blur-[12px] scale-110 brightness-[0.3]"
                src={`https://image.tmdb.org/t/p/original${movie.backdrop_path}`}
                alt="Backdrop Blurred"
              />
              <div className="absolute inset-0 bg-mask-radial pointer-events-none">
                <img
                  className="w-full h-full object-cover"
                  src={`https://image.tmdb.org/t/p/original${movie.backdrop_path}`}
                  alt="Backdrop Clear"
                />
              </div>
              <div className="absolute text-lg start-[85%] top-[50%] -translate-y-1/2 rotate-[270deg] w-max text-white opacity-70 tracking-wider">
                {movie.title} ({movie.release_date?.slice(0, 4)})
              </div>
            </div>
          )}

          {/* Center content */}
          <div className="absolute inset-0 flex flex-col tracking-wider newfont mb-[8rem] items-center justify-end z-10 text-center newcatchphrase">
            <h1 className="text-4xl md:text-5xl font-bold mb-2 uppercase">
              Where movies connect us.
            </h1>
            <p className="text-lg max-w-xl px-4 uppercase">
              Rate, review, comment, and explore community-made lists.
            </p>
          </div>
        </div>
        <div className=" flex  flex-row items-center justify-between w-5/6 z-20 relative">
          <div className="comment-class w-full flex flex-col items-center justify-center ">
            <div className="relative z-20 w-3/4">
              <section className="my-8">
                <h2 className="newfont text-transparent bg-gradient-to-r from-amber-400/90 to-yellow-400/90 bg-clip-text font-extrabold">
                  Popular reviews this week
                </h2>
                {topComments.length > 0 ? (
                  topComments.map((comment) => {
                    const isMovie = comment.Ismovie;
                    const name = isMovie ? comment.movieName : comment.tvName;
                    const poster = isMovie ? comment.movieImg : comment.tvImg;
                    const year =
                      (isMovie ? comment.movieDate : comment.tvDate) || "";
                    const posterUrl = `https://image.tmdb.org/t/p/original${poster}`;
                    return (
                      <div
                        key={comment.id || comment.reviewId}
                        className="flex items-start space-x-4 p-4 border-b border-gray-700"
                      >
                        {/* Poster */}
                        <img
                          src={posterUrl}
                          alt={name}
                          className="w-16 h-24 object-cover rounded"
                        />

                        {/* Content */}
                        <div>
                          {/* Title & Year */}
                          <div className="flex items-center space-x-2">
                            <h2 className="text-white font-bold text-lg">
                              {name}
                            </h2>
                            <span className="text-gray-400 text-sm">
                              {year}
                            </span>
                          </div>

                          {/* User name */}
                          <p className="text-gray-400 text-sm mb-1">
                            {comment.userName}
                          </p>

                          {/* Comment text */}
                          <p className="text-white mb-2">{comment.comment}</p>

                          {/* Upvote count */}
                          <div className="flex items-center text-gray-400 text-sm">
                            {comment.upvotes?.length || 0} upvotes
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p>No top comments this week yet.</p>
                )}
              </section>
            </div>
          </div>
          <div className="list-class">
            <div className="px-4">
              <div className="mt-8  z-40 relative newfont uppercase mb-1 text-lg font-extrabold md:text-xl lg:text-2xl 2xl:text-4xl  text-transparent bg-gradient-to-r from-amber-400/90 to-yellow-400/90 bg-clip-text w-full flex justify-center items-center">
                {" "}
                Popular lists 
              </div>
              {allLists.length > 0 ? (
                allLists.map((list) => (
                  <div
                    key={list.listId}
                    className="list-container z-40 px-4 relative bg-yellow-900 rounded-md bg-clip-padding backdrop-filter backdrop-blur-md bg-opacity-10 border my-8 border-gray-700"
                  >
                    <div className="flex flex-col px-4">
                      <div className="flex flex-row w-full items-center justify-between">
                        <div className="newfont uppercase mb-1 text-sm  md:text-md lg:text-lg 2xl:text-2xl inline-block text-transparent bg-gradient-to-r from-amber-400/90 to-yellow-400/90 bg-clip-text ">
                          {list.listName}
                        </div>
                        <button
                          className="bg-gradient-to-r from-amber-200 to-yellow-400 rounded text-xs newfonttwo px-1 h-max text-slate-50"
                          onClick={() => {
                            navigate("/listView", { state: list });
                          }}
                        >
                          see more{" "}
                        </button>
                      </div>

                      <hr className="h-px  bg-gray-200 border-0 dark:bg-gray-700"></hr>

                      {/* <div className="newfontwo uppercase mb-1 text-lg font-extrabold md:text-xl lg:text-2xl 2xl:text-4xl inline-block text-transparent bg-gradient-to-r from-amber-400/90 to-yellow-400/90 bg-clip-text ">Creator : {list.name}</div>  */}
                    </div>
                    <div className="">
                      <div className="flex relative overflow-x-hidden space-x-4">
                        {list.items.map((e, index) => (
                          <div
                            className="hover:z-50 "
                            style={{
                              marginLeft: index === 0 ? "0px" : "-90px",
                            }}
                          >
                            <MovieCard
                              movie={{
                                name: e.name,
                                id: e.tvid || e.movieid,
                                profile_path: e.posterImage,
                              }}
                              filterType={
                                e.movieid
                                  ? { isMovieChecked: true }
                                  : { isTvChecked: true }
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p></p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewHome;
